// src/components/Scheme/SchemePayWithdrawModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import toast from "react-hot-toast";
import { FaCheck, FaTimes, FaMoneyBillWave } from "react-icons/fa";
import { FiPrinter } from "react-icons/fi";
import {
  useAddSchemePaymentMutation,
  useWithdrawSchemeMutation,
} from "../../toolkitServices/customerTransactionApi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* ---------------- SalesList theme tokens ---------------- */
const BRAND = {
  header: "#9481ce",
  primary: "#6b59c6",
  primary2: "#7b66d6",
  ring: "#7c3aed",
};

/* ---------------- react-select styling (same as SalesList) ---------------- */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 10,
    borderColor: state.isFocused ? BRAND.ring : "#e5e7eb",
    boxShadow: state.isFocused ? `0 0 0 3px ${BRAND.ring}22` : "none",
    "&:hover": { borderColor: BRAND.ring },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
    fontWeight: 600,
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
      ? "#ddd6fe"
      : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 40 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

/* ---------------- animations ---------------- */
const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.15 } },
};
const pop = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2 } },
};

const modeOptions = [
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "UPI", label: "UPI" },
  { value: "Cheque", label: "Cheque" },
  { value: "RTGS", label: "RTGS" },
  { value: "Wallet", label: "Wallet" },
];

/* ---------------- numeric helpers ---------------- */
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round3 = (v) => Math.round(num(v) * 1000) / 1000;

const isDateMatured = (maturityDate) => {
  if (!maturityDate) return false;
  const d = new Date(maturityDate);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const end = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999
  );
  return now.getTime() >= end.getTime();
};

/* ---------------- response extraction ---------------- */
const pickLedgerId = (resp) => {
  const data = resp?.data ?? resp;
  return (
    data?.ledgerId ||
    data?._id ||
    data?.id ||
    data?.transactionId ||
    data?.receiptId ||
    data?.data?.ledgerId ||
    data?.data?._id ||
    data?.data?.id ||
    null
  );
};

const pickPrintUrl = (resp) => {
  const data = resp?.data ?? resp;
  return data?.printUrl || data?.data?.printUrl || null;
};



export default function SchemePayWithdrawModal({
  open,
  onClose,
  customerId,
  branchId,
  enrollment,
  action = "PAY", // PAY | WITHDRAW
  onSuccess,
  onPrintReceipt,
}) {
  const [addSchemePayment, payState] = useAddSchemePaymentMutation();
  const [withdrawScheme, wdState] = useWithdrawSchemeMutation();

  const isLoading = payState.isLoading || wdState.isLoading;

  const schemeType = String(enrollment?.schemeType || "").toUpperCase();
  const isGramScheme = schemeType === "GRAM";

  // withdraw allowed only if matured + all EMI paid (UI only)
  const paidInst = num(enrollment?.totalPaidInstallments || 0);
  const totalInst = num(enrollment?.installmentMonths || 0);
  const isFullyPaid =
    enrollment?.isFullyPaid != null
      ? !!enrollment.isFullyPaid
      : totalInst > 0
      ? paidInst >= totalInst
      : false;

  const isMatured =
    enrollment?.isMatured != null
      ? !!enrollment.isMatured
      : isDateMatured(enrollment?.maturityDate);

  const isWithdrawAllowed = isMatured && isFullyPaid;

  // payout calc (AMOUNT scheme)
  const paidAmount = num(enrollment?.totalPaidAmount);
  const benefitMonths = num(enrollment?.benefitsInMonths);
  const perMonthAmt = num(enrollment?.schemeAmountPerMonth);
  const defaultBenefitAmount = !isGramScheme ? perMonthAmt * benefitMonths : 0;

  const defaults = useMemo(
    () => ({
      mode: modeOptions[0],

      amount:
        action === "PAY"
          ? enrollment?.schemeAmountPerMonth
            ? String(enrollment.schemeAmountPerMonth)
            : ""
          : String(paidAmount + defaultBenefitAmount),

      benefitAmount: action === "WITHDRAW" ? String(defaultBenefitAmount) : "0",
      totalPayoutAmount:
        action === "WITHDRAW" ? String(paidAmount + defaultBenefitAmount) : "0",

      // GRAM only (PAY)
      ratePerGram: "",
      grams: "",

      installmentNo: "",
      refNo: "",
      note: "",
    }),
    [enrollment, action, paidAmount, defaultBenefitAmount, isGramScheme]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: defaults });

  // ✅ single modal, two views (FORM | PRINT)
  const [view, setView] = useState("FORM"); // FORM | PRINT
  const [lastReceipt, setLastReceipt] = useState(null);
  const [promptInfo, setPromptInfo] = useState({
    mode: "",
    amount: "",
    action: action,
  });

  useEffect(() => {
    if (open) {
      reset(defaults);
      setView("FORM");
      setLastReceipt(null);
      setPromptInfo({ mode: "", amount: "", action });
    } else {
      setView("FORM");
      setLastReceipt(null);
      setPromptInfo({ mode: "", amount: "", action });
    }
  }, [open, defaults, reset, action]);

  // ✅ Focus refs (FORM)
  const modeSelectRef = useRef(null);
  const amountInputRef = useRef(null);
  const benefitInputRef = useRef(null);
  const closeBtnRef = useRef(null);

  // ✅ Focus ref (PRINT)
  const yesPrintBtnRef = useRef(null);

  // ✅ focus management on FORM open
  useEffect(() => {
    if (!open) return;
    if (view !== "FORM") return;

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        if (action === "WITHDRAW") {
          if (!isWithdrawAllowed) {
            closeBtnRef.current?.focus?.();
            return;
          }
          if (modeSelectRef.current?.focus) {
            modeSelectRef.current.focus();
            return;
          }
          benefitInputRef.current?.focus?.();
          return;
        }

        if (modeSelectRef.current?.focus) {
          modeSelectRef.current.focus();
          return;
        }
        amountInputRef.current?.focus?.();
      });

      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [open, view, action, isWithdrawAllowed]);

  // ✅ focus management on PRINT open (Yes, Print)
  useEffect(() => {
    if (!open) return;
    if (view !== "PRINT") return;

    const raf = requestAnimationFrame(() => {
      yesPrintBtnRef.current?.focus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [open, view]);

  // auto-calc grams (PAY + GRAM)
  const wAmount = watch("amount");
  const wRate = watch("ratePerGram");

  useEffect(() => {
    if (!open) return;
    if (!isGramScheme || action !== "PAY") {
      setValue("grams", "");
      return;
    }
    const a = num(wAmount);
    const r = num(wRate);
    const g = r > 0 ? a / r : 0;
    setValue("grams", g > 0 ? String(round3(g)) : "");
  }, [open, isGramScheme, action, wAmount, wRate, setValue]);

  // WITHDRAW: benefit editable, other amounts locked
  const wBenefit = watch("benefitAmount");
  const benefitAmountNum = num(wBenefit);
  const totalPayoutAmount = paidAmount + benefitAmountNum;

  useEffect(() => {
    if (!open) return;
    if (action !== "WITHDRAW") return;
    setValue("totalPayoutAmount", String(totalPayoutAmount));
    setValue("amount", String(totalPayoutAmount)); // backend "amount" final payout
  }, [open, action, totalPayoutAmount, setValue, paidAmount]);

  const title = action === "WITHDRAW" ? "Scheme Withdraw" : "Scheme Payment";
  const sub =
    enrollment?.schemeName || enrollment?.schemeCode || enrollment?._id || "";
  const modeLabel = action === "WITHDRAW" ? "Payout Mode" : "Payment Mode";

  const onSubmit = async (v) => {
    if (!customerId) return toast.error("Select customer");
    if (!enrollment?._id) return toast.error("Select enrollment");
    if (!v.mode?.value) return toast.error("Select mode");

    if (action === "WITHDRAW" && !isWithdrawAllowed) {
      return toast.error(
        "Withdraw allowed only after scheme matured & all EMI paid."
      );
    }

    const amt = num(v.amount);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");

    const rate = num(v.ratePerGram);
    const grams = rate > 0 ? round3(amt / rate) : 0;

    if (action === "PAY" && isGramScheme) {
      if (!rate || rate <= 0) return toast.error("Enter valid rate per gram");
      if (!grams || grams <= 0) return toast.error("Grams calculation failed");
    }

    try {
      if (action === "WITHDRAW") {
        const resp = await withdrawScheme({
          customerId,
          branchId,
          enrollmentId: enrollment._id,
          mode: v.mode.value,
          amount: String(v.amount),
          refNo: v.refNo || "",
          note: v.note || "",
        }).unwrap();

        toast.success("Withdraw saved");

        const ledgerId = pickLedgerId(resp);
        const printUrl = pickPrintUrl(resp);

        // ✅ snapshot for prompt
        setPromptInfo({
          action: "WITHDRAW",
          mode: v.mode.value,
          amount: String(v.amount),
        });

        // ✅ if no print url, just close
        if (!ledgerId && !printUrl) {
          onSuccess?.();
          onClose?.();
          return;
        }

        setLastReceipt({ ledgerId, printUrl });
        setView("PRINT"); // ✅ FORM close -> PRINT open (same modal)
        return;
      }

      // PAY
      const resp = await addSchemePayment({
        customerId,
        branchId,
        enrollmentId: enrollment._id,
        installmentNo: v.installmentNo || undefined,
        mode: v.mode.value,
        amount: String(v.amount),
        grams: isGramScheme ? String(grams) : undefined,
        ratePerGram: isGramScheme ? String(v.ratePerGram) : undefined,
        refNo: v.refNo || "",
        note: v.note || "",
      }).unwrap();

      toast.success("Payment received");

      const ledgerId = pickLedgerId(resp);
      const printUrl = pickPrintUrl(resp);

      // ✅ snapshot for prompt
      setPromptInfo({
        action: "PAY",
        mode: v.mode.value,
        amount: String(v.amount),
      });

      if (!ledgerId && !printUrl) {
        onSuccess?.();
        onClose?.();
        return;
      }

      setLastReceipt({ ledgerId, printUrl });
      setView("PRINT"); // ✅ FORM close -> PRINT open (same modal)
    } catch (e) {
      const msg =
        e?.data?.message || e?.data?.msg || e?.error || "Something went wrong";
      toast.error(msg);
    }
  };

  const promptTitle =
    action === "WITHDRAW" ? "Withdraw Successful" : "Payment Successful";
  const promptAmountLabel = action === "WITHDRAW" ? "Payout" : "Amount";
  const canPrintPrompt = !!(lastReceipt?.ledgerId || lastReceipt?.printUrl);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/45 backdrop-blur-sm flex items-center justify-center p-3"
          variants={fade}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          <motion.div
            className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
            variants={pop}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce] text-white">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-extrabold tracking-tight">
                    {view === "PRINT" ? "Print Receipt" : title}
                  </h2>
                  <p className="text-[11px] text-white/90 mt-0.5 truncate">
                    {sub}
                    {enrollment?.enrollmentCode
                      ? ` · ${enrollment.enrollmentCode}`
                      : ""}
                    {schemeType ? ` · ${schemeType}` : ""}
                  </p>

                  {view === "FORM" && action === "WITHDRAW" && (
                    <div className="mt-2">
                      {isWithdrawAllowed ? (
                        <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold text-white border border-white/20">
                          Eligible: Scheme matured & all EMI paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold text-white border border-white/15">
                          Not eligible: complete EMI + maturity required
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <button
                  ref={closeBtnRef}
                  onClick={onClose}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1.5 text-[11px] font-semibold hover:bg-white/15"
                >
                  <FaTimes className="text-[10px]" />
                  Close
                </button>
              </div>
            </div>

            {/* BODY */}
            {view === "FORM" ? (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="p-4 space-y-3 max-h-[78vh] overflow-auto"
              >
                {action === "WITHDRAW" && !isWithdrawAllowed && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2">
                    <div className="text-[12px] font-extrabold text-amber-800">
                      Withdraw not allowed yet
                    </div>
                    <div className="text-[11px] text-amber-700 mt-0.5">
                      Scheme withdraw tabhi hoga jab <b>maturity date pass</b>{" "}
                      ho aur <b>saare EMI paid</b> ho.
                    </div>
                  </div>
                )}

                {action === "WITHDRAW" && (
                  <div className="rounded-2xl border bg-gradient-to-b from-white to-slate-50 p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Paid Amount (fixed)
                        </label>
                        <input
                          disabled
                          value={paidAmount ? paidAmount.toFixed(2) : "0.00"}
                          className="w-full px-3 py-2 border rounded-xl text-[12px] bg-white/70 text-slate-700"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Benefit Amount (editable)
                        </label>
                        <input
                          ref={benefitInputRef}
                          type="number"
                          step="0.01"
                          {...register("benefitAmount", {
                            validate: (vv) =>
                              num(vv) >= 0 || "Benefit must be >= 0",
                          })}
                          placeholder="Enter benefit"
                          disabled={!isWithdrawAllowed}
                          className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30 disabled:bg-slate-100 disabled:text-slate-500"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                          Suggested: {benefitMonths || 0} month × ₹
                          {perMonthAmt || 0} = ₹
                          {defaultBenefitAmount.toFixed(2)}
                        </p>
                        {errors.benefitAmount && (
                          <p className="text-red-500 text-[11px] mt-1">
                            {errors.benefitAmount.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Total Payout (auto)
                        </label>
                        <input
                          disabled
                          value={
                            totalPayoutAmount
                              ? totalPayoutAmount.toFixed(2)
                              : "0.00"
                          }
                          className="w-full px-3 py-2 border rounded-xl text-[12px] bg-white/70 text-slate-800 font-semibold"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                          total = paid + benefit
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      {modeLabel} <span className="text-rose-500">*</span>
                    </label>
                    <Controller
                      control={control}
                      name="mode"
                      rules={{ required: "Mode is required" }}
                      render={({ field }) => {
                        const { ref, ...rest } = field;
                        return (
                          <Select
                            {...rest}
                            ref={(el) => {
                              modeSelectRef.current = el;
                              ref?.(el);
                            }}
                            options={modeOptions}
                            styles={selectStyles}
                            menuPortalTarget={menuPortalTarget}
                            placeholder={
                              action === "WITHDRAW"
                                ? "Select payout mode"
                                : "Select payment mode"
                            }
                            isClearable={false}
                            menuPlacement="auto"
                            isDisabled={
                              action === "WITHDRAW" && !isWithdrawAllowed
                            }
                          />
                        );
                      }}
                    />
                    {errors.mode && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors.mode.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      {action === "WITHDRAW"
                        ? "Payout Amount (₹)"
                        : "Amount (₹)"}{" "}
                      <span className="text-rose-500">*</span>
                    </label>
                    <input
                      ref={amountInputRef}
                      type="number"
                      step="0.01"
                      {...register("amount", {
                        required: "Amount is required",
                        validate: (vv) => num(vv) > 0 || "Amount must be > 0",
                      })}
                      placeholder={
                        action === "WITHDRAW" ? "Payout amount" : "Enter amount"
                      }
                      readOnly={action === "PAY" || action === "WITHDRAW"}
                      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30 disabled:bg-slate-100 disabled:text-slate-500"
                    />
                    {errors.amount && (
                      <p className="text-red-500 text-[11px] mt-1">
                        {errors.amount.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* GRAM scheme */}
                {action === "PAY" && isGramScheme && (
                  <div className="rounded-2xl border bg-gradient-to-b from-white to-slate-50 p-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Rate per Gram (₹){" "}
                          <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register("ratePerGram", {
                            required: "Rate per gram is required",
                            validate: (vv) =>
                              num(vv) > 0 || "Rate must be > 0",
                          })}
                          placeholder="e.g. 6800"
                          className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30"
                        />
                        {errors.ratePerGram && (
                          <p className="text-red-500 text-[11px] mt-1">
                            {errors.ratePerGram.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                          Grams (auto)
                        </label>
                        <input
                          disabled
                          {...register("grams")}
                          placeholder="0.000"
                          className="w-full px-3 py-2 border rounded-xl text-[12px] bg-white/70 text-slate-700"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                          grams = amount / rate
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {action === "PAY" && (
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Installment No (optional)
                    </label>
                    <input
                      {...register("installmentNo")}
                      placeholder="e.g. 1"
                      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Ref No
                    </label>
                    <input
                      {...register("refNo")}
                      placeholder="UTR / Cheque / Txn Id"
                      disabled={action === "WITHDRAW" && !isWithdrawAllowed}
                      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30 disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                      Note
                    </label>
                    <input
                      {...register("note")}
                      placeholder="Optional note"
                      disabled={action === "WITHDRAW" && !isWithdrawAllowed}
                      className="w-full px-3 py-2 border rounded-xl text-[12px] focus:outline-none focus:ring-2 focus:ring-[#6b59c6]/30 disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </div>
                </div>

                <div className="pt-1 flex flex-col sm:flex-row gap-2 justify-end">
                  <motion.button
                    whileHover={{ scale: isLoading ? 1 : 1.02, y: -1 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98, y: 0 }}
                    type="submit"
                    disabled={
                      isLoading ||
                      (action === "WITHDRAW" && !isWithdrawAllowed)
                    }
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-[#6b59c6] px-5 py-2 text-[12px] font-semibold text-white shadow-sm hover:opacity-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                      <FaCheck className="text-[10px]" />
                    </span>
                    {isLoading
                      ? "Saving..."
                      : action === "WITHDRAW"
                      ? "Confirm Withdraw"
                      : "Receive Payment"}
                  </motion.button>

                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-5 py-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <FaTimes className="text-[10px]" /> Cancel
                  </button>
                </div>

                <div className="text-[10px] text-slate-500 flex items-center gap-2">
                  <FaMoneyBillWave className="text-[11px]" />
                  Entry save hote hi niche transactions list me reflect hogi.
                </div>
              </form>
            ) : (
              // ✅ PRINT VIEW (same modal, no second overlay)
              <div className="p-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-[11px]">
                  <div className="text-slate-600">
                    <span className="font-semibold text-slate-800">Scheme:</span>{" "}
                    {enrollment?.schemeName || enrollment?.schemeCode || "-"}
                  </div>
                  <div className="text-slate-600 mt-1">
                    <span className="font-semibold text-slate-800">
                      {promptAmountLabel}:
                    </span>{" "}
                    ₹ {Number(promptInfo?.amount || 0).toFixed(2)}
                  </div>
                  <div className="text-slate-600 mt-1">
                    <span className="font-semibold text-slate-800">Mode:</span>{" "}
                    {promptInfo?.mode || "-"}
                  </div>
                </div>

                {!canPrintPrompt ? (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                    Receipt link not available from API response.
                  </div>
                ) : (
                  <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-[11px] text-emerald-800">
                    {promptTitle}. Would you like to print receipt?
                  </div>
                )}

                <div className="mt-3 flex flex-col sm:flex-row flex-wrap gap-2 justify-end">
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => {
                      setLastReceipt(null);
                      setView("FORM"); // reset view for next open (optional)
                      onSuccess?.();
                      onClose?.();
                    }}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full border border-slate-300 bg-slate-50 px-4 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                  >
                    No, thanks
                  </button>

                  <button
                    ref={yesPrintBtnRef}
                    type="button"
                    disabled={isLoading || !canPrintPrompt}
                   onClick={() => {
  onPrintReceipt?.(lastReceipt?.ledgerId);

  setLastReceipt(null);
  setView("FORM");
  onSuccess?.();
  onClose?.();
}}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl sm:rounded-full bg-[#6b59c6] px-4 py-2 text-[11px] font-semibold text-white hover:opacity-95 disabled:opacity-60"
                  >
                    <FiPrinter className="text-[14px]" />
                    Yes, Print
                  </button>
                </div>

                <div className="mt-2 text-[10px] text-slate-500">
                  Receipt will open in a new tab.
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
